import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { UserModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box } from '@mui/system'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'login', label: 'Identifiant' },
  { value: 'firstName', label: 'Prénom' },
  { value: 'lastName', label: 'Nom' },
  { value: 'email', label: 'Email' },
  { value: 'accountState', label: 'État' },
  { value: 'admin', label: 'Admin (true/false)' },
  { value: 'license_key', label: 'Clé de licence' },
]

export default function UserDataGrid({ users }: { users: UserModel[] }) {
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [filteredUsers, setFilteredUsers] = React.useState(users)
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  )
  const navigate = useNavigate()

  React.useEffect(() => {
    const newRows = filteredUsers.map((value) => ({
      ...value,
      id: value.login,
      lastConnectionDate: value.lastConnectionDate
        ? dayjs.unix(value.lastConnectionDate).toDate()
        : null,
      creationDate: dayjs.unix(value.creationDate).toDate(),
      flagDate: value.flagDate ? dayjs.unix(value.flagDate).toDate() : null, // Ajouté
      license_key: value.license_key || 'N/A', // Ajouté
    }))
    setRows(newRows)
  }, [filteredUsers])

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate(`/user/${id}`)
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    if (
      window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${id} ?`)
    ) {
      axios
        .delete(`${base_url}/user/${id}`)
        .then(() => {
          toast.success(`Utilisateur ${id} marqué pour suppression.`)
          setRows((prevRows) => prevRows.filter((row) => row.login !== id))
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })
  }

  const handleInlineEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ) => {
    try {
      const payload = {
        admin: newRow.admin,
        firstName: newRow.firstName,
        lastName: newRow.lastName,
        email: newRow.email,
        accountState: newRow.accountState,
      }
      const response = await axios.put(
        `${base_url}/user/${oldRow.login}`,
        payload
      )
      toast.success('Utilisateur mis à jour.')
      return response.data
    } catch (error) {
      console.error('Failed to update user:', error)
      return oldRow // Revert changes on fail
    }
  }

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  const columns: GridColDef[] = [
    {
      field: 'admin',
      type: 'boolean',
      headerName: 'Admin',
      editable: true,
      width: 90,
    },
    {
      field: 'login',
      headerName: 'Identifiant',
      editable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'firstName',
      headerName: 'Prénom',
      editable: true,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastName',
      headerName: 'Nom',
      editable: true,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'email',
      headerName: 'Email',
      editable: true,
      flex: 1.5,
      minWidth: 200,
    },
    { field: 'accountState', headerName: 'État', editable: true, width: 100 },
    {
      field: 'flagDate', // Ajouté
      headerName: 'Date de suppression',
      type: 'dateTime',
      width: 180,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ]
        }

        return [
          <GridActionsCellItem
            icon={<PreviewIcon />}
            label="View"
            onClick={handlePreviewClick(id)}
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleInlineEditClick(id)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
          />,
        ]
      },
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setFilteredUsers}
          fields={searchFields}
          originalData={users}
        />
        <ExportButton
          fields={[
            'admin',
            'login',
            'firstName',
            'lastName',
            'email',
            'accountState',
            'lastConnectionDate',
            'creationDate',
            'flagDate', // Ajouté
            'license_key', // Ajouté
          ]}
          data={users}
          exportFileName="users_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        getRowId={(row) => row.login}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />
    </Box>
  )
}
